import { useCallback, useEffect, useState } from 'react';
import { usePagedList } from '../../hooks/usePagedList';
import { ListCount, LoadMore } from './AdminPaging';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Badge,
  Box,
  Button,
  HStack,
  Link,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { adminApi, ApiError, type InviteRequest } from '../../lib/api';
import { appLocale } from '../../lib/format';

// Requests for an invitation (M44, docs/invitation-requests.md D116). To
// invite, create the family and its first root's QR code in the Families
// tab, then mark the request invited.

const STATUS: Record<
  InviteRequest['status'],
  { label: string; scheme: string }
> = {
  new: { label: 'New', scheme: 'orange' },
  invited: { label: 'Invited', scheme: 'green' },
  declined: { label: 'Declined', scheme: 'gray' },
};

const SIZE: Record<string, string> = {
  '2-5': '2–5 people',
  '6-10': '6–10 people',
  '11-20': '11–20 people',
  '20+': 'More than 20',
};

function RequestCard({
  request,
  onChange,
}: {
  request: InviteRequest;
  onChange: () => Promise<void>;
}) {
  const toast = useToast();
  const [note, setNote] = useState(request.adminNote ?? '');
  const [busy, setBusy] = useState(false);

  const act = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await work();
      await onChange();
      toast({ title: done, status: 'success', duration: 2000 });
    } catch (err) {
      toast({
        title: 'Could not save',
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const status = STATUS[request.status];
  return (
    <Box
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={4}
    >
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="flex-start" flexWrap="wrap">
          <Box>
            <Text fontWeight={800} fontSize="18px">
              {request.name}
            </Text>
            <Link
              href={`mailto:${request.email}`}
              color="accent"
              fontWeight={600}
            >
              {request.email}
            </Link>
          </Box>
          <HStack>
            <Badge
              colorScheme={status.scheme}
              borderRadius="full"
              px={2}
              textTransform="none"
            >
              {status.label}
            </Badge>
            <Text fontSize="sm" color="muted">
              {new Date(request.createdAt).toLocaleDateString(appLocale())}
            </Text>
          </HStack>
        </HStack>
        <Text fontSize="sm" color="muted">
          {SIZE[request.familySize] ?? request.familySize}
        </Text>
        {request.note && <Text whiteSpace="pre-wrap">{request.note}</Text>}
        <Textarea
          size="sm"
          value={note}
          maxLength={1000}
          placeholder="Your note (only you see this)"
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (note !== (request.adminNote ?? '')) {
              void act(
                () =>
                  adminApi.updateInviteRequest(request.id, {
                    note: note || null,
                  }),
                'Note saved'
              );
            }
          }}
        />
        <HStack spacing={2} flexWrap="wrap">
          {request.status !== 'invited' && (
            <Button
              size="sm"
              isDisabled={busy}
              onClick={() =>
                void act(
                  () =>
                    adminApi.updateInviteRequest(request.id, {
                      status: 'invited',
                    }),
                  'Marked invited'
                )
              }
            >
              Mark invited
            </Button>
          )}
          {request.status !== 'declined' && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              isDisabled={busy}
              onClick={() =>
                void act(
                  () =>
                    adminApi.updateInviteRequest(request.id, {
                      status: 'declined',
                    }),
                  'Marked declined'
                )
              }
            >
              Decline
            </Button>
          )}
          {request.status !== 'new' && (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="gray"
              isDisabled={busy}
              onClick={() =>
                void act(
                  () =>
                    adminApi.updateInviteRequest(request.id, { status: 'new' }),
                  'Moved back to new'
                )
              }
            >
              Undo
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            color="danger"
            isDisabled={busy}
            onClick={() =>
              void act(
                () => adminApi.deleteInviteRequest(request.id),
                'Request deleted'
              )
            }
          >
            Delete
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}

export function RequestsTab({
  onCount,
}: {
  onCount?: (newCount: number) => void;
}) {
  // 50 a page, newest first (#73).
  const fetchPage = useCallback(
    async (cursor: string | null) =>
      (await adminApi.getInviteRequests({ cursor })).data,
    []
  );
  const list = usePagedList<
    InviteRequest,
    Awaited<ReturnType<typeof fetchPage>>
  >(fetchPage);
  const requests = list.items;
  const newCount = list.extra?.newCount;
  useEffect(() => {
    if (newCount !== undefined) onCount?.(newCount);
  }, [newCount, onCount]);

  const loadingState = useLoadingState({
    loading: !requests && !list.error,
    hasData: requests !== null,
    error: list.error,
    retry: list.retry,
  });
  if (!requests || loadingState.show)
    return <LoadingPanel state={loadingState} />;
  return (
    <VStack align="stretch" spacing={4} maxW="760px">
      <Text color="muted">
        People asking for Twinden for their family, from
        twinden.com/request-invite. To invite someone, create their family and
        its first root’s QR code in the Families tab, send it to them, then mark
        the request invited. Answered requests are deleted after 90 days.
      </Text>
      <ListCount list={list} one="request" many="requests" />
      {requests.length === 0 ? (
        <Text color="muted">No requests yet.</Text>
      ) : (
        requests.map((request) => (
          <RequestCard
            key={request.id}
            request={request}
            onChange={list.reload}
          />
        ))
      )}
      <LoadMore list={list} />
    </VStack>
  );
}
